'use client';

import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { twJoin } from 'tailwind-merge';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { useBanner } from '@/ui/providers/banner-provider';
import { AnimatePresence, motion } from 'framer-motion';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import {
  getBannerAlignment,
  getBannerLayout,
  getOfferEndedColors,
  hasRichTextContent,
  shouldHideBannerOnPage,
} from '@/ui/components/layout/banner/banner-utils';
import { shouldRenderOnPage } from '@/integrations/crystallize/helpers/page-targeting-helper';
import { ClockIcon } from '@/ui/components/shared/icons/clock-icon';
import OptimizedImage from '../../shared/OptimizedImage';
import ButtonLink from '../../shared/ButtonLink';
import { RichTextRenderer } from '../../tileRenderers/RichTextRenderer';
import { BannerTile } from './types/banner-tile';
import { shouldHideHeaderOnPage } from '../header/utils';
import { BannerCountdown } from './banner-countdown';

type CountdownExpiryBehavior = 'hide' | 'offer-ended';

// Background is chosen from the CMS `color` selection; `isDark` drives the close-button contrast.
// Button styling — and the OFFER ENDED pill derived from it — are owned by the CTA's own variant.
interface BackgroundVariant {
  className: string;
  isDark: boolean;
}

const BACKGROUND_VARIANTS: Record<string, BackgroundVariant> = {
  primary: { className: 'bg-primary-100 text-neutral-100', isDark: false },
  neutral: { className: 'bg-neutral-100 text-white', isDark: true },
  white: { className: 'bg-white text-neutral-100', isDark: false },
};

interface BannerProps {
  tile?: BannerTile;
  isInHeader?: boolean;
}

// Clears a stuck :focus/:active state (e.g. the CTA after Back navigation restores from bfcache).
function blurActiveElement() {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
}

function isDatePast(datetime?: string | null): boolean {
  return Boolean(datetime) && new Date(datetime as string).getTime() <= Date.now();
}

function BannerCloseButton({
  isDark,
  onClose,
}: Readonly<{ isDark: boolean; onClose: () => void }>) {
  return (
    <button
      aria-label="Close Banner"
      type="button"
      className={clsx('group absolute right-3 top-3 rounded-xs p-1 duration-200 ease-in', {
        'hover:bg-neutral-70': isDark,
        'hover:bg-neutral-5 hover:opacity-50': !isDark,
      })}
      onClick={onClose}
    >
      <XMarkIcon
        className={clsx('h-4 w-auto cursor-pointer duration-200 ease-in', {
          'text-white': isDark,
          'text-neutral-50 opacity-50 group-hover:opacity-75': !isDark,
        })}
      />
    </button>
  );
}

// All the reasons the banner should not render, kept in one place so the guard branching doesn't
// inflate the component's cognitive complexity.
function isBannerHiddenOnPage(
  currentPath: string,
  tile: BannerTile,
  isInHeader: boolean | undefined,
): boolean {
  if (shouldHideHeaderOnPage(currentPath) || shouldHideBannerOnPage(currentPath)) {
    return true;
  }

  // Unpublished / empty banner.
  if (Object.keys(tile).length === 0) {
    return true;
  }

  // CMS page targeting: Include list (if present) wins over Exclude list.
  if (!shouldRenderOnPage(currentPath, tile['include-pages'], tile['exclude-pages'])) {
    return true;
  }

  // The banner is mounted in both the header and the layout; only the instance matching the
  // campaign's stick-to-top placement renders.
  const stickToTop = Boolean(tile['stick-to-top']?.value);

  return (Boolean(isInHeader) && !stickToTop) || (!isInHeader && stickToTop);
}

export function Banner({ tile, isInHeader }: Readonly<BannerProps>) {
  const currentPath = usePathname();
  const t = useTranslations('Banner');
  const { isBannerClosed, setIsBannerClosed } = useBanner();
  // Seed from the countdown date so an already-expired campaign renders its final state on the
  // first paint instead of briefly flashing the live countdown before the timer's onExpire fires.
  const [isCountdownExpired, setIsCountdownExpired] = useState(() =>
    isDatePast(tile?.['countdown-date']?.datetime),
  );

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        blurActiveElement();
      }
    };

    globalThis.addEventListener('pageshow', handlePageShow);
    return () => globalThis.removeEventListener('pageshow', handlePageShow);
  }, []);

  if (!tile || isBannerHiddenOnPage(currentPath, tile, isInHeader)) {
    return null;
  }

  const { image, title, subtitle, cta } = tile;
  const currentDate = new Date();
  const startDate = new Date(tile['start-date']?.datetime);
  const endDate = new Date(tile['end-date']?.datetime);
  const stickToTop = tile['stick-to-top']?.value;
  const background =
    BACKGROUND_VARIANTS[tile.color?.options?.[0]?.value ?? 'primary'] ??
    BACKGROUND_VARIANTS.primary;

  const countdownDatetime = tile['countdown-date']?.datetime;
  const expiryBehavior =
    (tile['countdown-behavior-on-expiry']?.options?.[0]?.value as CountdownExpiryBehavior) ||
    'offer-ended';

  const isLeftAligned = getBannerAlignment(tile, Boolean(image)) === 'left';
  const hasTitle = hasRichTextContent(title);
  const hasSubtitle = hasRichTextContent(subtitle);
  const hasCtaConfigured = Boolean(cta?.components?.length);
  // Mini banner: only a subtitle, nothing else → renders as a slim bar with smaller text.
  const isMini = hasSubtitle && !hasTitle && !image && !countdownDatetime && !hasCtaConfigured;

  const showCountdown = Boolean(countdownDatetime) && !isCountdownExpired;
  const isOfferEnded = isCountdownExpired && expiryBehavior === 'offer-ended';
  // Countdown reached zero and the campaign is configured to auto-hide.
  const isCountdownHidden = isCountdownExpired && expiryBehavior === 'hide';
  const hasCta = !isOfferEnded && hasCtaConfigured;
  const offerEndedText = tile['offer-ended-button-text']?.text || t('offerEnded');

  const layout = getBannerLayout({
    isLeftAligned,
    hasImage: Boolean(image),
    showCountdown,
    hasCta,
    isOfferEnded,
  });

  // Rendered once; placed either below the text or in the right split-slot per the layout flags.
  const countdownElement = showCountdown ? (
    <BannerCountdown
      expiryDate={new Date(countdownDatetime)}
      onExpire={() => setIsCountdownExpired(true)}
    />
  ) : null;

  const offerEndedPill = isOfferEnded ? (
    <span
      className="flex w-fit items-center gap-2 rounded-full border-2 px-5 py-3 text-sm font-bold uppercase tracking-wide"
      style={getOfferEndedColors(cta)}
    >
      <ClockIcon className="h-4 w-4 shrink-0" />
      {offerEndedText}
    </span>
  ) : null;

  const ctaButton = hasCta ? (
    <ButtonLink ctaComponent={cta} className="w-fit" onClick={blurActiveElement} />
  ) : null;

  const isWithinDateWindow = currentDate >= startDate && currentDate <= endDate;
  const isBannerVisible = !isBannerClosed && !isCountdownHidden && isWithinDateWindow;

  return (
    <AnimatePresence mode="popLayout">
      {isBannerVisible && (
        <motion.div
          style={{ zIndex: 0 }}
          exit={{ opacity: 0, y: -50, transition: { duration: 0.4 } }}
          className={twJoin(
            'relative z-80 px-4 sm:px-8',
            isMini ? 'py-3' : 'py-6',
            stickToTop && '-mx-4 -mt-6 mb-6 sm:-mx-8',
            background.className,
          )}
        >
          <div className="mx-auto w-full max-w-screen-2xl">
            <div className={layout.row}>
              {image && (
                // Fixed box height (not sm:h-auto) so the reserved space doesn't depend on the
                // image's declared aspect ratio — otherwise, above the fold, the browser reserves a
                // tall square box before the (wide) image loads and the banner visibly shrinks in.
                // `priority` loads it eagerly (it's the banner's LCP image).
                <div className="h-25 sm:h-48 sm:w-2/4 md:w-1/4">
                  <OptimizedImage
                    imageComponent={image}
                    priority
                    className="mx-auto h-full w-auto object-contain sm:ml-auto sm:mr-0"
                    width={200}
                    height={200}
                    sizes="200px"
                  />
                </div>
              )}
              <div className={layout.content}>
                {hasTitle && <RichTextRenderer blocks={title} className="mb-2 text-xl font-bold" />}
                {hasSubtitle && (
                  <RichTextRenderer blocks={subtitle} className={isMini ? 'text-sm' : 'text-lg'} />
                )}

                {countdownElement && !layout.countdownRight && (
                  <div className="mt-4">{countdownElement}</div>
                )}
                {offerEndedPill && !layout.offerEndedRight && (
                  <div className="mt-6">{offerEndedPill}</div>
                )}
                {ctaButton && !layout.ctaRight && <div className="mt-6">{ctaButton}</div>}
              </div>

              {layout.isSplit && (
                <div className="shrink-0">
                  {layout.countdownRight && countdownElement}
                  {layout.ctaRight && ctaButton}
                  {layout.offerEndedRight && offerEndedPill}
                </div>
              )}
            </div>
          </div>
          <BannerCloseButton isDark={background.isDark} onClose={() => setIsBannerClosed(true)} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
